'use client';

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import ContinueLendo from '@/components/obra/ContinueLendo';
import { OBRAS_GENEROS, HOME_FORMATO_PADRAO } from '@/constants/obras-generos';

const TODOS = { id: 'todos', label: 'Todos', formato: 0 as const, color: '#f97316' };

function parseFormatoParam(raw: string | null): number {
    if (raw == null || raw === '') return 0;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.trunc(n);
}

function isKnownFormato(n: number): boolean {
    return n === 0 || OBRAS_GENEROS.some((g) => g.formato === n);
}

export default function ContinueLendoPage() {
    const { isAuthenticated } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const formatoId = parseFormatoParam(searchParams.get('formato'));
    const queryFromUrl = searchParams.get('q') ?? '';
    const [searchDraft, setSearchDraft] = useState(queryFromUrl);

    const paginaParsed = parseInt(searchParams.get('pagina') || '1', 10);
    const pagina = Number.isFinite(paginaParsed) && paginaParsed > 0 ? paginaParsed : 1;

    useEffect(() => {
        setSearchDraft(queryFromUrl);
    }, [queryFromUrl]);

    useEffect(() => {
        const t = window.setTimeout(() => {
            const q = searchDraft.trim();
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (q) next.set('q', q);
                    else next.delete('q');
                    next.set('pagina', '1');
                    return next;
                },
                { replace: true },
            );
        }, 350);
        return () => window.clearTimeout(t);
    }, [searchDraft, setSearchParams]);

    useEffect(() => {
        if (isKnownFormato(formatoId)) return;
        const next = new URLSearchParams(searchParams.toString());
        next.set('formato', String(HOME_FORMATO_PADRAO));
        if (!next.get('pagina')) next.set('pagina', '1');
        setSearchParams(next, { replace: true });
    }, [formatoId, searchParams, setSearchParams]);

    const setFormato = (f: number) => {
        const next = new URLSearchParams(searchParams.toString());
        next.set('formato', String(f));
        next.set('pagina', '1');
        setSearchParams(next, { replace: true });
    };

    const formatos = [TODOS, ...OBRAS_GENEROS] as const;

    if (!isAuthenticated) {
        return (
            <main className="min-h-screen bg-black px-4 pb-24 pt-24">
                <div className="mx-auto max-w-lg rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
                    <p className="text-white/80">Inicia sessão para veres o Continue Lendo.</p>
                    <Link
                        to="/entrar"
                        className="mt-6 inline-flex rounded-full border border-orange-500/50 bg-orange-500/15 px-6 py-2.5 text-sm font-semibold text-orange-200 transition-colors hover:bg-orange-500/25"
                    >
                        Entrar
                    </Link>
                </div>
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-black pb-28 pt-20 md:pb-24 md:pt-24">
            <div className="mx-auto max-w-7xl px-4 md:px-8">
                <h1 className="mb-6 text-2xl font-bold text-white md:text-3xl">Continue Lendo</h1>

                <div className="mb-6 space-y-3">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                        <input
                            value={searchDraft}
                            onChange={(e) => setSearchDraft(e.target.value)}
                            placeholder="Buscar obra no Continue Lendo"
                            className="w-full rounded-2xl border border-white/10 bg-black/50 py-3 pl-10 pr-4 text-base text-white outline-none backdrop-blur-xl placeholder:text-white/35 focus:border-orange-500/45"
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                        {formatos.map((f) => {
                            const active = formatoId === f.formato;
                            return (
                                <button
                                    key={f.id}
                                    type="button"
                                    onClick={() => setFormato(f.formato)}
                                    className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                        active ? 'text-white' : 'border-white/10 bg-white/[0.04] text-white/65 hover:text-white'
                                    }`}
                                    style={active ? { backgroundColor: f.color, borderColor: f.color } : undefined}
                                >
                                    {f.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <ContinueLendo showAll formatoId={formatoId} searchQuery={queryFromUrl} pagina={pagina} />
            </div>
        </main>
    );
}
